import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  GoogleMap,
  Marker,
  DirectionsRenderer,
  DirectionsService,
  InfoWindow,
  useJsApiLoader
} from '@react-google-maps/api';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axiosInstance, { MAPKEY } from '../../../axiosInstance';
import toast from 'react-hot-toast';
import { useThemeContext } from '../../../fetchdata/ThemeContext';
import { useCurrentPosition } from 'react-use-geolocation';
import mytaxi from '../../assets/img/delivery.webp';

/** ---------------- Helpers ---------------- */
const getCoord = (obj, keys = []) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== '') return Number(v);
  }
  return null;
};



const extractStartEnd = (order) => {
  if (!order) return { start: null, end: null };

  // Try several common naming patterns used across backends
  const startLat = getCoord(order, ['startLat', 'pickupLat', 'fromLat', 'sourceLat', 'start_lat', 'pickup_lat']);
  const startLng = getCoord(order, ['startLng', 'pickupLng', 'fromLng', 'sourceLng', 'start_lng', 'pickup_lng']);
  const endLat   = getCoord(order, ['endLat', 'dropLat', 'toLat', 'destinationLat', 'end_lat', 'drop_lat']);
  const endLng   = getCoord(order, ['endLng', 'dropLng', 'toLng', 'destinationLng', 'end_lng', 'drop_lng']);

  const start = startLat && startLng ? { lat: startLat, lng: startLng } : null;
  const end   = endLat && endLng ? { lat: endLat, lng: endLng } : null;
  return { start, end };
};

const mapContainerStyle = { width: '100%', height: '100vh' };

const defaultOptions = {
  gestureHandling: 'greedy',
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  zoomControl: true,
  clickableIcons: false,
  styles: [
    // Global desaturation and lighter look
    { elementType: 'geometry', stylers: [{ saturation: -100 }, { lightness: 20 }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#4a4a4a' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },

    // Roads: light gray
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#e6e6e6' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#555555' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#d9d9d9' }] },

    // Water: white
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#8f8f8f' }] },

    // Parks/landscape: very light gray
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#fafafa' }] },

    // Hide most POIs for a cleaner white look
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  ],
};


/** ---------------- Component ---------------- */
const MapOrderView = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // Google Maps loader (replace any LoadScript with this)
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: MAPKEY,
  });

  const [Order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const { sendMessage, getAuth } = useThemeContext();

  // Live geolocation
  const [position, geoError] = useCurrentPosition(); // optional, just to surface errors
  const [currentMapLocation, setCurrentMapLocation] = useState(null);

  // Map / markers / directions
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [routeRequest, setRouteRequest] = useState(null);
  const [directions, setDirections] = useState(null);

  // Follow-me / interaction guards to prevent blinking
  const [followMe, setFollowMe] = useState(true);
  const [userInteracting, setUserInteracting] = useState(false);
  const userInteractTimer = useRef(null);

  // UI
  const [submitBusy, setSubmitBusy] = useState(false);

  /** Fetch order (once) */
  const getUserOrders = async () => {
    try {
      const { data } = await axiosInstance.get(`/user-orders-view/${id}`);
      if (!data?.success) {
        toast.error('Order not found');
        navigate('/all-order');
        return;
      }

      const raw = data.userOrder;

      // Pull start from getAuth (your current user’s location) if available
      const startLatFromAuth = getAuth?.latitude;
      const startLngFromAuth = getAuth?.longitude;

      // Pull end from the order payload if available
      const endLatFromOrder = raw?.latitude;
      const endLngFromOrder = raw?.longitude;

      console.log('startLng:', startLngFromAuth, 'startLat:', startLatFromAuth, 'endLng:', endLngFromOrder, 'endLat:', endLatFromOrder);

      // Normalize everything to the common keys once
      const normalized = {
        ...raw,
        startLat: Number(startLatFromAuth ?? raw?.startLat ?? raw?.pickupLat ?? raw?.fromLat ?? raw?.sourceLat ?? raw?.start_lat ?? raw?.pickup_lat ?? ''),
        startLng: Number(startLngFromAuth ?? raw?.startLng ?? raw?.pickupLng ?? raw?.fromLng ?? raw?.sourceLng ?? raw?.start_lng ?? raw?.pickup_lng ?? ''),
        endLat:   Number(endLatFromOrder   ?? raw?.endLat   ?? raw?.dropLat   ?? raw?.toLat     ?? raw?.destinationLat ?? raw?.end_lat   ?? raw?.drop_lat   ?? ''),
        endLng:   Number(endLngFromOrder   ?? raw?.endLng   ?? raw?.dropLng   ?? raw?.toLng     ?? raw?.destinationLng ?? raw?.end_lng   ?? raw?.drop_lng   ?? ''),
      };

      // Guard NaN → null
      if (Number.isNaN(normalized.startLat)) normalized.startLat = null;
      if (Number.isNaN(normalized.startLng)) normalized.startLng = null;
      if (Number.isNaN(normalized.endLat))   normalized.endLat   = null;
      if (Number.isNaN(normalized.endLng))   normalized.endLng   = null;

      setOrder(normalized);
    } catch (error) {
      console.error(error);
      toast.error('Order not found');
      navigate('/all-order');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (getAuth) getUserOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuth]);

  /** High-accuracy live location via native watchPosition WITH movement threshold */
  useEffect(() => {
    let lastLocation = null;
    const MIN_MOVE_METERS = 10;

    const toRad = (d) => (d * Math.PI) / 180;
    const distanceMeters = (a, b) => {
      if (!a || !b) return Infinity;
      const R = 6371000;
      const dLat = toRad(b.lat - a.lat);
      const dLng = toRad(b.lng - a.lng);
      const la1 = toRad(a.lat);
      const la2 = toRad(b.lat);
      const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
      return 2 * R * Math.asin(Math.sqrt(h));
    };

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const newLoc = { lat: latitude, lng: longitude };

        if (!lastLocation || distanceMeters(lastLocation, newLoc) >= MIN_MOVE_METERS) {
          setCurrentMapLocation(newLoc);
          lastLocation = newLoc; // update reference
        }
      },
      (err) => console.error('Geolocation error:', err),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  /** Smoothly pan to live point when follow is ON and user isn't interacting */
  const panToLive = useCallback(() => {
    if (!mapRef.current || !currentMapLocation) return;
    mapRef.current.panTo(currentMapLocation);
  }, [currentMapLocation]);

  useEffect(() => {
    if (!mapRef.current || !currentMapLocation) return;
    if (!followMe) return;
    if (userInteracting) return;
    panToLive();
  }, [currentMapLocation?.lat, currentMapLocation?.lng, followMe, userInteracting, panToLive]);

  const markInteracting = useCallback(() => {
    setUserInteracting(true);
    if (userInteractTimer.current) clearTimeout(userInteractTimer.current);
    userInteractTimer.current = setTimeout(() => setUserInteracting(false), 1500);
  }, []);

  /** Emit socket message whenever live location changes (no throttling here; add if needed) */
  useEffect(() => {
    if (!Order || !currentMapLocation) return;
    try {
      sendMessage({
        userId: Order?.userId,
        currentLocation: currentMapLocation,
        type: 'location',
        orderId: Order?.orderId || Order?._id || id
      });
    } catch (e) {
      console.warn('sendMessage failed:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMapLocation?.lat, currentMapLocation?.lng]);

  /** Marker icons */
  const liveMarkerIcon = useMemo(() => {
    if (isLoaded && window.google?.maps) {
      return { url: mytaxi, scaledSize: new window.google.maps.Size(70, 70) };
    }
    return undefined;
  }, [isLoaded]);

  const startIcon = useMemo(() => {
    if (!isLoaded || !window.google?.maps) return undefined;
    return {
      path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
      fillColor: '#28a745',
      fillOpacity: 0.9,
      strokeWeight: 1,
      strokeColor: '#1e7e34',
      scale: 6
    };
  }, [isLoaded]);

  const endIcon = useMemo(() => {
    if (!isLoaded || !window.google?.maps) return undefined;
    return {
      path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
      fillColor: '#dc3545',
      fillOpacity: 0.9,
      strokeWeight: 1,
      strokeColor: '#a71d2a',
      scale: 6
    };
  }, [isLoaded]);

  /** Fit all markers into view */
  const fitAll = useCallback(() => {
    try {
      if (!mapRef.current || !window.google?.maps) return;
      const map = mapRef.current;
      const { start, end } = extractStartEnd(Order);

      const bounds = new window.google.maps.LatLngBounds();
      let added = false;

      if (start) { bounds.extend(start); added = true; }
      if (end) { bounds.extend(end); added = true; }
      if (currentMapLocation) { bounds.extend(currentMapLocation); added = true; }

      if (added) map.fitBounds(bounds, { top: 100, right: 100, bottom: 100, left: 100 });
    } catch (e) {
      console.warn('fitAll error', e);
    }
  }, [Order, currentMapLocation]);

  /** Compute route request whenever points change */
  useEffect(() => {
    const { start, end } = extractStartEnd(Order);

    if (!start && !end) {
      setRouteRequest(null);
      setDirections(null);
      return;
    }

    if (start && end) {
      setRouteRequest({ origin: start, destination: end, travelMode: 'DRIVING' });
    } else if (currentMapLocation && (start || end)) {
      const dest = start || end;
      setRouteRequest({ origin: currentMapLocation, destination: dest, travelMode: 'DRIVING' });
    } else {
      setRouteRequest(null);
      setDirections(null);
    }
  }, [Order, currentMapLocation]);

  /** Fit after map is ready */
  useEffect(() => {
    if (!mapLoaded) return;
    fitAll();
  }, [mapLoaded, fitAll]);

  /** Update Order via your existing API */
  const UserUpdateData = async () => {
    if (!Order) return;
    setSubmitBusy(true);
    try {
      const res = await axiosInstance.put(`/admin/update-full-order/${id}`, Order);
      if (res?.data?.success) {
        toast.success('Data updated successfully!');
        getUserOrders();
      } else {
        toast.error('Update failed.');
      }
    } catch (error) {
      console.error('Update error', error);
      toast.error('Error updating order');
    } finally {
      setSubmitBusy(false);
    }
  };

  const openInGoogleMaps = () => {
    const { start, end } = extractStartEnd(Order);
    if (start && end) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&origin=${start.lat},${start.lng}&destination=${end.lat},${end.lng}&travelmode=driving`,
        '_blank'
      );
    } else if (currentMapLocation && (start || end)) {
      const dest = start || end;
      window.open(
        `https://www.google.com/maps/dir/?api=1&origin=${currentMapLocation.lat},${currentMapLocation.lng}&destination=${dest.lat},${dest.lng}&travelmode=driving`,
        '_blank'
      );
    } else {
      toast.error('Missing start/end coordinates.');
    }
  };

  const { start, end } = extractStartEnd(Order);

  /** -------- Render guards for loader ---------- */
  if (loadError) {
    return <div className="alert alert-danger m-3">Maps failed to load. Check API key & restrictions.</div>;
  }
  if (!isLoaded) {
    return <div className="m-3">Loading…</div>;
  }

  /** ---------------- UI + Map ---------------- */
  return (
    <>
      {/* Follow / Fit controls (optional) */}
      <div className="position-absolute bottom-0 start-0 m-3" style={{ zIndex: 20 }}>
        <div className="btn-group">
          <button
            className={`btn btn-sm ${followMe ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFollowMe((v) => !v)}
            title="Toggle follow me"
          >
            {followMe ? 'Following You' : 'Follow Me'}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={fitAll} title="Fit to markers">
            Fit
          </button>
        </div>
      </div>

      {/* Top overlay UI - Bootstrap */}
      <div className="position-absolute w-100" style={{ zIndex: 10, pointerEvents: 'none' }}>
        <div className="container pt-3">
          <div className="row g-3">
            <div className="col-12 col-lg-5" style={{ pointerEvents: 'auto' }}>
              <div className="card shadow-sm border rounded-4">
                <div className="card-body  p-md-2 p-2">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                   
                  <div className="small text-muted ">
                    Order ID:&nbsp;
                    <span className="text-dark fw-semibold">{Order?.orderId || Order?._id || '-'}</span>
                  </div>
                    <span className={`badge ${isLoading ? 'bg-secondary' : 'bg-success'}`}>
                      {isLoading ? 'Loading' : 'Live'}
                    </span>
                  </div>


                  <div className="row g-2">
                    <div className="col-12">
                      <div className="p-2 rounded-3 bg-light">
                        <div className="d-flex align-items-center">
                          <div className="me-2 rounded-circle" style={{ width: 10, height: 10, background: '#28a745' }} />
                          <div className="d-flex gap-2 align-items-center">
                            <div className="fw-semibold">Start</div>
                            <div className="small text-muted">
                              {start ? `${start.lat.toFixed(5)}, ${start.lng.toFixed(5)}` : '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="p-2 rounded-3 bg-light">
                        <div className="d-flex align-items-center">
                          <div className="me-2 rounded-circle" style={{ width: 10, height: 10, background: '#dc3545' }} />
                          <div className="d-flex gap-2 align-items-center">
                            <div className="fw-semibold">End</div>
                            <div className="small text-muted">
                              {end ? `${end.lat.toFixed(5)}, ${end.lng.toFixed(5)}` : '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 d-none">
                      <div className="p-2 rounded-3 bg-light">
                        <div className="d-flex align-items-center">
                          <img src={mytaxi} alt="live" style={{ width: 22, height: 22 }} className="me-2" />
                          <div className="flex-grow-1">
                            <div className="fw-semibold">Your Live Location</div>
                            <div className="small text-muted">
                              {currentMapLocation
                                ? `${currentMapLocation.lat.toFixed(5)}, ${currentMapLocation.lng.toFixed(5)}`
                                : '—'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm "
                      onClick={openInGoogleMaps}
                      disabled={!(start || end) && !currentMapLocation}
                    >
                    <svg
                    width={20}
                    height={20}
  xmlns="http://www.w3.org/2000/svg"
  xmlnsXlink="http://www.w3.org/1999/xlink"
  aria-label="Google Maps"
  role="img"
  viewBox="0 0 512 512"
>
  <rect id="a" width={512} height={512} x={0} y={0} rx="15%" fill="#ffffff" />
  <clipPath id="b">
    <use xlinkHref="#a" />
  </clipPath>
  <g clipPath="url(#b)">
    <path fill="#35a85b" d="M0 512V0h512z" />
    <path fill="#5881ca" d="M256 288L32 512h448z" />
    <path fill="#c1c0be" d="M288 256L512 32v448z" />
    <path stroke="#fadb2a" strokeWidth={71} d="M0 512L512 0" />
    <path
      fill="none"
      stroke="#f2f2f2"
      strokeWidth={22}
      d="M175 173h50a50 54 0 1 1-15-41"
    />
    <path
      fill="#de3738"
      d="M353 85a70 70 0 0 1 140 0c0 70-70 70-70 157 0-87-70-87-70-157"
    />
    <circle cx={423} cy={89} r={25} fill="#7d2426" />
  </g>
</svg> Open Maps

                    </button>

                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={fitAll}
                    >
                                          <i className="ri-shape-2-line"/>

                    </button>

                    <button
                      type="button"
                      className="btn btn-success btn-sm ms-auto"
                      onClick={UserUpdateData}
                      disabled={submitBusy}
                    >
                      {submitBusy ? 'Saving…' : 'Save Order'}
                    </button>

                  </div>

                  {geoError && (
                    <div className="alert alert-warning mt-3 py-2 mb-0">
                      Couldn’t read GPS: {geoError.message}
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>      

      {/* Map (IMPORTANT: no `center` – we use defaultCenter once; then pan when needed) */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        defaultCenter={start || { lat: 28.6139, lng: 77.2090 }} // initial only
        zoom={14}
        options={defaultOptions}
        onLoad={(map) => { mapRef.current = map; setMapLoaded(true); }}
        onDragStart={markInteracting}
        onDragEnd={markInteracting}
        onZoomChanged={markInteracting}
      >
        {/* Start Marker */}
        {start && (
          <Marker
            position={start}
            icon={startIcon}
            onClick={() => setSelectedMarker({ type: 'start', position: start })}
          />
        )}

        {/* End Marker */}
        {end && (
          <Marker
            position={end}
            icon={endIcon}
            onClick={() => setSelectedMarker({ type: 'end', position: end })}
          />
        )}

        {/* Live Marker */}
        {currentMapLocation && (
          <Marker
            position={currentMapLocation}
            icon={liveMarkerIcon}
            onClick={() => setSelectedMarker({ type: 'live', position: currentMapLocation })}
          />
        )}

        {/* InfoWindow */}
        {selectedMarker && (
          <InfoWindow
            position={selectedMarker.position}
            onCloseClick={() => setSelectedMarker(null)}
          >
            <div className="p-1">
              <div className="fw-semibold mb-1">
                {selectedMarker.type === 'start' && 'Start Location'}
                {selectedMarker.type === 'end' && 'End Location'}
                {selectedMarker.type === 'live' && 'Your Live Location'}
              </div>
              <div className="small text-muted">
                {selectedMarker.position.lat.toFixed(6)}, {selectedMarker.position.lng.toFixed(6)}
              </div>
              {selectedMarker.type !== 'live' && (
                <div className="mt-2">
                  <Link to="#" className="badge text-bg-primary text-decoration-none">View</Link>
                </div>
              )}
            </div>
          </InfoWindow>
        )}

        {/* Directions – preserveViewport so it doesn’t fight user gestures */}
        {routeRequest && (
          <DirectionsService
            options={routeRequest}
            callback={(res, status) => {
              if (status === 'OK') {
                setDirections(res);
              } else {
                setDirections(null);
              }
            }}
          />
        )}
        {directions && (
          <DirectionsRenderer
            options={{
              directions,
              preserveViewport: true,   // keeps your zoom/pan intact
              suppressMarkers: true,    // use your own markers
            }}
          />
        )}
      </GoogleMap>
    </>
  );
};

export default MapOrderView;
